import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import { Baby, ClipboardList, AlarmClock, CalendarCheck, GripVertical } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const joinDash = (to, pathname) =>
  to.startsWith('/dashboard') ? to : (pathname.startsWith('/dashboard') && to.startsWith('/')) ? `/dashboard${to}` : to;

const DASHBOARD_NAME = 'academics';

const baseSections = [
  { label: 'Undergraduate Academic / स्नातक शैक्षणिक', path: '/student', icon: <Baby size={32} /> },
  { label: 'Postgraduate Academic / स्नातकोत्तर शैक्षणिक', path: '/intern-assessment', icon: <ClipboardList size={32} /> },
  { label: 'Faculty Tools / फैकल्टी टूल्स', path: '/faculty-tools', icon: <AlarmClock size={32} /> },
  { label: 'Academic Timetable / शैक्षणिक समय सारणी', path: '/timetable', icon: <CalendarCheck size={32} /> },
].map(section => ({ ...section, roles: ['faculty', 'intern', 'junior resident', 'senior resident', 'admin', 'medical student'] }));

function SortableCard({ section, pathname }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.path });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : 'auto', opacity: isDragging ? 0.7 : 1 };

  return (
    <div ref={setNodeRef} style={style}>
      <Link to={joinDash(section.path, pathname)} className="no-underline">
        <Card className="rounded-2xl shadow-md hover:shadow-xl bg-white dark:bg-[#26272b] border border-gray-200 dark:border-[#363640] transition-colors duration-300 ease-in-out cursor-pointer">
          <CardContent className="flex flex-col items-center justify-center py-8 relative">
            <span {...attributes} {...listeners} className="absolute top-2 right-2 cursor-grab active:cursor-grabbing" title="Drag to reorder">
              <GripVertical size={20} className="text-indigo-700 dark:text-indigo-300 opacity-70" />
            </span>
            <div className="mb-3 text-indigo-700 dark:text-indigo-300 transition-colors">
              {section.icon}
            </div>
            <Typography variant="h6" align="center" className="text-gray-800 dark:text-gray-100 transition-colors">
              {section.label}
            </Typography>
          </CardContent>
        </Card>
      </Link>
    </div>
  );
}

const GroupAcademics = () => {
  const user = JSON.parse(localStorage.getItem('loggedInUser')) || {};
  const role = (user?.role || user?.userType || '').toLowerCase();
  const visibleSectionsInit = baseSections.filter(section => section.roles.map(r => r.toLowerCase()).includes(role));
  const [sections, setSections] = useState(visibleSectionsInit);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    async function fetchOrder() {
      try {
        const res = await axios.get(`/api/dashboard-config/${DASHBOARD_NAME}/${role}`);
        if (res.data.order && Array.isArray(res.data.order)) {
          const map = Object.fromEntries(visibleSectionsInit.map(sec => [sec.path, sec]));
          const ordered = res.data.order.map(path => map[path]).filter(Boolean);
          const extra = visibleSectionsInit.filter(sec => !res.data.order.includes(sec.path));
          setSections([...ordered, ...extra]);
        } else {
          setSections(visibleSectionsInit);
        }
      } catch {
        setSections(visibleSectionsInit);
      } finally { setLoading(false); }
    }
    fetchOrder();
    // eslint-disable-next-line
  }, [role]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex(sec => sec.path === active.id);
    const newIndex = sections.findIndex(sec => sec.path === over.id);
    const newOrder = arrayMove(sections, oldIndex, newIndex);
    setSections(newOrder);
    try {
      await axios.post(`/api/dashboard-config/${DASHBOARD_NAME}/${role}`, { order: newOrder.map(sec => sec.path) });
    } catch {}
  };

  return (
    <div className="p-6 bg-white dark:bg-[#232326] rounded-xl shadow border border-gray-200 dark:border-[#363640] min-h-screen transition-colors">
      <Typography variant="h6" className="text-indigo-800 dark:text-indigo-300 font-semibold mb-4">Academic Modules</Typography>
      {loading ? (
        <div className="text-gray-500 dark:text-gray-300 text-center mt-20">Loading...</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sections.map(section => section.path)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {sections.map((section) => (<SortableCard key={section.path} section={section} pathname={location.pathname} />))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};
export default GroupAcademics;
